import React from 'react';
import { Icon } from '../../../components/base/Icon';
import { getIconDefinition } from '../../../components/IconSelector';
import { Tooltip } from '../../../components/overlays/Tooltip';
import { resolveThemeColor } from '../../../constants/theme-aware-colors';
import { useTheme, useThemeColors } from '../../../providers/ThemeProvider';
import { useListLinkNavigation } from '../../hooks/useListLinkNavigation';
import type {
	EnhancedFieldConfiguration,
	ListBuilderCallbacks,
	Size,
	TableRow,
} from '../../types';
import { getFieldDisplayProperties } from './displayHelpers';

interface IconDisplayProps {
	field: EnhancedFieldConfiguration;
	value: any;
	data: TableRow;
	listKey?: string;
	objects?: Array<{ objectTable: string; baseUrl?: string }>;
	callbacks?: ListBuilderCallbacks;
}

// Size mappings for icons ('xxs'/'xl' fall back to md)
const sizeMap: Partial<Record<Size, number>> = {
	xs: 12,
	sm: 14,
	md: 16,
	lg: 20,
};

export function IconDisplay({
	field,
	value,
	data,
	listKey,
	objects,
	callbacks,
}: IconDisplayProps) {
	const colors = useThemeColors();
	const { isDark } = useTheme();
	const { styleConfig, link } = field;

	// Resolve the link the same way the text/badge/button cells do, so an icon
	// cell can be a real anchor rather than a bare button: a resolved href powers
	// middle/cmd/ctrl-click "open in new tab", and a normal left-click runs the
	// SPA navigation + saves list context so the destination's back button
	// returns to this list. object/self/custom(+urlTemplate) targets all resolve;
	// callback links have no URL and stay button-invoked.
	const { linkUrl, handleLinkClick, isCallback } = useListLinkNavigation({
		field,
		data,
		listKey,
		objects,
		callbacks,
	});

	// Get display value, mapped icon name, and color using value mapping
	const {
		displayValue,
		iconName: mappedIconName,
		colorValue,
	} = getFieldDisplayProperties(field, value, data);
	// Get icon name - prioritize mapped icon, then styleConfig icon, then displayValue if it's an icon name
	const iconName =
		mappedIconName ||
		styleConfig?.iconName ||
		(typeof displayValue === 'string' ? displayValue : null);

	// Get tooltip text from styleConfig
	const tooltipText = styleConfig?.tooltip;

	if (!iconName) {
		return null;
	}

	// Get icon definition
	const iconDefinition = getIconDefinition(iconName);
	if (!iconDefinition) {
		return null;
	}

	// Get color from value mapping or color rule with theme-aware resolution
	const resolvedText = colorValue?.text
		? resolveThemeColor(colorValue.text, isDark)
		: null;
	const resolvedBg = colorValue?.bg
		? resolveThemeColor(colorValue.bg, isDark)
		: null;
	// A named meaning resolves to classes, not a colour value; an icon takes
	// the meaning's text class (the badge classes would paint a fill behind
	// it). Without either, the icon keeps the quiet secondary colour.
	const intentClassName =
		resolvedText == null && resolvedBg == null
			? colorValue?.textClassName
			: undefined;
	const iconColor =
		resolvedText ??
		resolvedBg ??
		(intentClassName != null ? undefined : colors.values.textSecondary);

	// Get opacity from color rule
	const iconOpacity = colorValue?.opacity;

	// Get size
	const iconSize =
		(styleConfig?.size != null ? sizeMap[styleConfig.size] : undefined) ?? 16;

	const iconElement = (
		<Icon
			icon={iconDefinition}
			size={iconSize}
			color={iconColor}
			className={intentClassName}
			style={iconOpacity != null ? { opacity: iconOpacity } : undefined}
		/>
	);

	const isLinkEnabled = link?.enabled === true;
	const clickableClasses =
		'flex items-center justify-center p-1 hover:opacity-70 transition-opacity cursor-pointer';

	let content: React.ReactNode;
	if (isLinkEnabled && !isCallback && linkUrl != null) {
		// Navigation link — render a true anchor. href enables native new-tab on
		// modified clicks; handleLinkClick does the SPA navigation on a plain click.
		content = (
			<a href={linkUrl} onClick={handleLinkClick} className={clickableClasses}>
				{iconElement}
			</a>
		);
	} else if (isLinkEnabled && isCallback) {
		// Callback link — no URL to put on an anchor; keep it a button that fires
		// the configured callback.
		content = (
			<button
				onClick={handleLinkClick}
				className={clickableClasses}
				type="button"
			>
				{iconElement}
			</button>
		);
	} else {
		// No link (decorative icon), or a link whose target couldn't be resolved
		// for this row — render non-interactive.
		content = (
			<div className="flex items-center justify-center">{iconElement}</div>
		);
	}

	if (tooltipText != null && tooltipText !== '') {
		return <Tooltip content={tooltipText}>{content}</Tooltip>;
	}

	return content;
}
